import React, { useState, useEffect } from "react";
import {
  Switch,
  Button,
  Snackbar,
  Alert,
  Slide,
  Grid,
  Avatar,
  Typography,
  Box,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Link,
  Chip,
  Fade,
  IconButton,
} from "@mui/material";
import { ExpandMore, ExpandLess } from "@mui/icons-material";
import SoftBox from "components/SoftBox";
import SoftTypography from "components/SoftTypography";
import AnseraLogo from "assets/images/Anser.png";
import axios from "axios";
import { useDomain } from "context/DomainContext";
import { FAST_API_BASE_URL } from "constants";
import { LAMBDA_BASE_URL } from "constants";

const integrationFeatures = [
  {
    name: "Google Calendar",
    value: "google-calendar",
    logo: "https://upload.wikimedia.org/wikipedia/commons/a/a5/Google_Calendar_icon_%282020%29.svg",
    description: "Sync calendar events and schedule meetings seamlessly",
    category: "Productivity",
    google_calendar_mcp: false,
  },
];

const Integrations = () => {
  const [featureStates, setFeatureStates] = useState(
    integrationFeatures.reduce((acc, f) => {
      acc[f.name] = false;
      return acc;
    }, {})
  );

  const [expandedFeatures, setExpandedFeatures] = useState(
    integrationFeatures.reduce((acc, f) => {
      acc[f.name] = false;
      return acc;
    }, {})
  );

  const { domainId } = useDomain();

  const [snackbar, setSnackbar] = useState({
    open: false,
    severity: "success",
    message: "",
  });
  const [helpDialogOpen, setHelpDialogOpen] = useState(false);
  const [googleConnected, setGoogleConnected] = useState(false);
  const [loading, setLoading] = useState(false);
  const [checkingStatus, setCheckingStatus] = useState(true);

  useEffect(() => {
    const fetchInitialStatus = async () => {
      try {
        setCheckingStatus(true);
        const mcpRes = await axios.get(`${LAMBDA_BASE_URL}/mcp-status`, {
          headers: { "domain-token": domainId },
        });

        if (mcpRes?.data?.result) {
          const mcpEnabled = mcpRes.data.result.google_calendar_mcp === 1;
          setFeatureStates((prev) => ({
            ...prev,
            "Google Calendar": mcpEnabled,
          }));

          integrationFeatures[0].google_calendar_mcp = mcpEnabled;
          if (mcpEnabled) {
            await checkGoogleCredentials();
          } else {
            setGoogleConnected(false);
          }
        }
      } catch (err) {
        console.error("Failed to fetch initial MCP status:", err);
        setFeatureStates((prev) => ({
          ...prev,
          "Google Calendar": false,
        }));
        setGoogleConnected(false);
      } finally {
        setCheckingStatus(false);
      }
    };

    fetchInitialStatus();
  }, [domainId]);


  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const googleAuth = params.get('google_auth');
    
    if (googleAuth === 'success') {
      setGoogleConnected(true);
      setSnackbar({
        open: true,
        severity: "success",
        message: "Google Calendar connected successfully!",
      });
      checkGoogleCredentials();
      window.history.replaceState({}, document.title, window.location.pathname);
    } else if (googleAuth === 'error') {
      setGoogleConnected(false);
      setSnackbar({
        open: true,
        severity: "error",
        message: "Failed to connect Google Calendar. Please try again.",
      });
      window.history.replaceState({}, document.title, window.location.pathname);
    }
  }, []);
  const checkGoogleCredentials = async () => {
    try {
      const res = await axios.get(`${FAST_API_BASE_URL}/api/mcp-credentials/google-calendar`, {
        headers: { "DOMAIN-TOKEN": domainId },
      });
      
      if (res?.data?.result) {
        const credentials = res.data.result;
        const hasValidCredentials = credentials.client_id && 
                                   credentials.client_secret && 
                                   credentials.client_id.trim() !== '' && 
                                   credentials.client_secret.trim() !== '';
        
        setGoogleConnected(hasValidCredentials);
        
        if (!hasValidCredentials) {
          console.warn("Google Calendar credentials are missing or empty");
        }
      } else {
        setGoogleConnected(false);
      }
    } catch (err) {
      console.error("Failed to check Google credentials:", err);
      setGoogleConnected(false);
    }
  };

  const handleExpand = (name, value) => {
    setExpandedFeatures((prev) => {
      const isExpanding = !prev[name]; 
      if (isExpanding && value === "google-calendar" && featureStates[name]) {
        checkGoogleCredentials();
      }
      return {
        ...prev,
        [name]: isExpanding,
      };
    });
  };

  const handleToggle = async (name, value) => {
    const newValue = !featureStates[name];
    setFeatureStates((prev) => ({
      ...prev,
      [name]: newValue,
    }));
    integrationFeatures[0].google_calendar_mcp = newValue;
    if (newValue && value === "google-calendar") {
      setExpandedFeatures((prev) => ({
        ...prev,
        [name]: true,
      }));
      setTimeout(() => {
        checkGoogleCredentials();
      }, 500);
    } else if (!newValue) {

      setGoogleConnected(false);
    }

    try {
      await axios.patch(
        `${LAMBDA_BASE_URL}/mcp-status`,
        [{ google_calendar_mcp: newValue ? 1 : 0 }],
        { headers: { "domain-token": domainId } }
      );
      setSnackbar({
        open: true,
        severity: "success",
        message: `Google Calendar ${newValue ? "enabled" : "disabled"} successfully!`,
      });
    } catch (err) {
      console.error("Failed to update MCP status:", err);
      setSnackbar({
        open: true,
        severity: "error",
        message: "Failed to update Google Calendar status.",
      });
      setFeatureStates((prev) => ({
        ...prev,
        [name]: !newValue,
      }));
      integrationFeatures[0].google_calendar_mcp = !newValue;
    }
  };

  const handleConnectGoogle = async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${FAST_API_BASE_URL}/api/google-calendar/auth`, {
        headers: { "DOMAIN-TOKEN": domainId },
      });
      
      if (res?.data?.result?.auth_url) {
        window.location.href = res.data.result.auth_url;
      } else {
        throw new Error("No auth URL received");
      }
    } catch (err) {
      console.error("Failed to initiate Google auth:", err);
      setSnackbar({
        open: true,
        severity: "error",
        message: "Failed to connect to Google Calendar. Please try again.",
      });
      setLoading(false);
    }
  };

  const handleDisconnectGoogle = async () => {
    setLoading(true);
    try {
      await axios.delete(`${FAST_API_BASE_URL}/api/google-calendar/disconnect`, {
        headers: { "DOMAIN-TOKEN": domainId },
      });
      
      setGoogleConnected(false);
      setSnackbar({
        open: true,
        severity: "success",
        message: "Google Calendar disconnected successfully!",
      });
    } catch (err) {
      console.error("Failed to disconnect Google:", err);
      setSnackbar({
        open: true,
        severity: "error",
        message: "Failed to disconnect Google Calendar.",
      });
    } finally {
      setLoading(false);
    }
  };

  const handleSnackbarClose = () => {
    setSnackbar({ ...snackbar, open: false });
  };

  return (
    <SoftBox
      sx={{
        backgroundColor: "#fff",
        p: 4,
        borderRadius: "20px",
        boxShadow: "0px 10px 40px rgba(0,0,0,0.05)",
        minHeight: "500px",
      }}
    >
      <Snackbar
        open={snackbar.open}
        autoHideDuration={4000}
        onClose={handleSnackbarClose}
        anchorOrigin={{ vertical: "top", horizontal: "center" }}
        TransitionComponent={(props) => <Slide {...props} direction="down" />}
      >
        <Alert
          onClose={handleSnackbarClose}
          severity={snackbar.severity}
          variant="filled"
          sx={{
            backgroundColor: snackbar.severity === "success" ? "#219297" : "#d32f2f",
            color: "#fff",
            borderRadius: "12px",
            boxShadow: "0px 4px 20px rgba(0,0,0,0.15)",
          }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>

      <SoftBox mb={4}>
        <SoftBox display="flex" alignItems="center" gap={2} mb={1}>
          <Box
            sx={{
              borderRadius: "12px",
              p: 1,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
            }}
          >
            <img src={AnseraLogo} alt="Ansera Logo" width={64} height={30} />
          </Box>
          <SoftTypography variant="h5" fontWeight="bold" color="dark">
            Manage Integrations
          </SoftTypography>
        </SoftBox>
        <SoftTypography variant="body2" color="text" sx={{ opacity: 0.7, mb: 2 }}>
          Enable or disable integrations with third-party tools.
        </SoftTypography>
        <Box sx={{ height: "2px", width: "30%", background: "linear-gradient(90deg, #219297 0%, transparent 100%)" }} />
      </SoftBox>

      <Grid container spacing={3}>
        {integrationFeatures.map((feature) => (
          <Grid item xs={12} key={feature.name}>
            <Fade in timeout={500}>
              <SoftBox
                sx={{
                  border: "1px solid",
                  borderColor: featureStates[feature.name] ? "#219297" : "#e5f1f1",
                  borderRadius: "16px",
                  p: 2,
                  transition: "all 0.3s ease",
                  background: "linear-gradient(135deg, #f0fafa 0%, #fff 100%)",
                  "&:hover": {
                    boxShadow: "0px 8px 24px rgba(33, 146, 151, 0.12)",
                    transform: "translateY(-2px)",
                  },
                }}
              >
                <SoftBox display="flex" alignItems="center" justifyContent="space-between" mb={1}>
                  <SoftBox display="flex" alignItems="center" gap={2}>
                    <Avatar
                      src={feature.logo}
                      alt={feature.name}
                      sx={{
                        width: 48,
                        height: 48,
                        boxShadow: "0px 4px 12px rgba(0,0,0,0.08)",
                        background: "#fff",
                        p: 0.5,
                      }}
                      variant="rounded"
                    />
                    <Box>
                      <SoftBox display="flex" alignItems="center" gap={1.5}>
                        <SoftTypography variant="h6" fontWeight="bold">
                          {feature.name}
                        </SoftTypography>
                        {featureStates[feature.name] && (
                          <Chip
                            label="Active"
                            size="small"
                            sx={{
                              height: "20px",
                              backgroundColor: "#219297",
                              color: "#fff",
                              fontSize: "0.7rem",
                              fontWeight: 600,
                            }}
                          />
                        )}
                      </SoftBox>
                      <SoftTypography variant="caption" color="text" sx={{ opacity: 0.7 }}>
                        {feature.description}
                      </SoftTypography>
                    </Box>
                  </SoftBox>
                  <Box display="flex" alignItems="center" gap={2}>
                    <Switch
                      checked={featureStates[feature.name]}
                      onChange={() => handleToggle(feature.name, feature.value)}
                      disabled={loading || checkingStatus}
                      sx={{
                        "& .MuiSwitch-switchBase.Mui-checked": { color: "#219297" },
                        "& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track": { backgroundColor: "#219297" },
                        "& .MuiSwitch-track": { backgroundColor: "#e0e0e0" },
                      }}
                    />
                    <IconButton 
                      onClick={() => handleExpand(feature.name, feature.value)}
                      disabled={loading}
                    >
                      {expandedFeatures[feature.name] ? <ExpandLess /> : <ExpandMore />}
                    </IconButton>
                  </Box>
                </SoftBox>

                {feature.name === "Google Calendar" && expandedFeatures["Google Calendar"] && (
                  <Fade in timeout={300}>
                    <Box mt={3} pt={3} sx={{ borderTop: "1px solid #e5f1f1" }}>
                      <SoftBox display="flex" alignItems="center" justifyContent="space-between" mb={3}>
                        <Typography variant="subtitle1" fontWeight={700} sx={{ color: "#219297" }}>
                          Connection Status
                        </Typography>
                        {/* <Link
                          component="button"
                          variant="body2"
                          underline="none"
                          sx={{
                            color: "#219297",
                            fontWeight: 600,
                            display: "flex",
                            alignItems: "center",
                            gap: 0.5,
                            "&:hover": { opacity: 0.8 },
                          }}
                          onClick={() => setHelpDialogOpen(true)}
                        >
                          <span style={{ fontSize: "1.1rem" }}>ℹ️</span> Need help?
                        </Link> */}
                      </SoftBox>

                      <SoftBox mt={3} display="flex" alignItems="center" gap={2}>
                        {checkingStatus ? (
                          <Typography variant="body2" sx={{ color: "#666", fontStyle: "italic" }}>
                            Checking connection status...
                          </Typography>
                        ) : !googleConnected ? (
                          <Button
                            variant="contained"
                            onClick={handleConnectGoogle}
                            disabled={loading || !featureStates[feature.name]}
                            sx={{
                              background: "linear-gradient(135deg, #219297 0%, #1a767a 100%)",
                              color: "#fff",
                              fontWeight: "bold",
                              textTransform: "none",
                              borderRadius: "10px",
                              px: 3,
                              py: 1,
                              boxShadow: "0px 4px 12px rgba(33, 146, 151, 0.3)",
                              "&:hover": {
                                background: "linear-gradient(135deg, #1a767a 0%, #145d61 100%)",
                                boxShadow: "0px 6px 16px rgba(33, 146, 151, 0.4)",
                              },
                              "&:disabled": {
                                background: "#e0e0e0",
                                color: "#9e9e9e",
                              },
                            }}
                          >
                            {loading ? "Connecting..." : "Connect to Google Calendar"}
                          </Button>
                        ) : (
                          <SoftBox display="flex" alignItems="center" gap={2}>
                            <Box
                              sx={{
                                display: "flex",
                                alignItems: "center",
                                gap: 1,
                                backgroundColor: "#e8f5e9",
                                px: 2.5,
                                py: 1,
                                borderRadius: "10px",
                              }}
                            >
                              <Typography variant="body2" fontWeight={600} sx={{ color: "#2e7d32" }}>
                                Connected to Google Calendar
                              </Typography>
                            </Box>
                          </SoftBox>
                        )}
                      </SoftBox>

                      {googleConnected && (
                        <SoftBox mt={2}>
                          <Typography variant="body2" sx={{ color: "#666", fontStyle: "italic" }}>
                            Your Google Calendar is connected and ready to sync events seamlessly.
                          </Typography>
                        </SoftBox>
                      )}

                      {!featureStates[feature.name] && (
                        <SoftBox mt={2}>
                          <Typography variant="body2" sx={{ color: "#f44336", fontStyle: "italic" }}>
                            Enable the toggle above to connect Google Calendar.
                          </Typography>
                        </SoftBox>
                      )}
                    </Box>
                  </Fade>
                )}
              </SoftBox>
            </Fade>
          </Grid>
        ))}
      </Grid>

      <Dialog 
        open={helpDialogOpen} 
        onClose={() => setHelpDialogOpen(false)} 
        maxWidth="md" 
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: "20px",
            boxShadow: "0px 20px 60px rgba(0,0,0,0.15)",
          }
        }}
      >
        <DialogTitle sx={{ 
          fontWeight: "bold", 
          color: "#219297",
          fontSize: "1.5rem",
          pb: 1,
        }}>
           Google Calendar Setup Guide
        </DialogTitle>
        <DialogContent dividers sx={{ p: 3 }}>
          <Typography variant="body2" sx={{ mb: 3, color: "#555" }}>
            Follow these simple steps to integrate Google Calendar with Ansera:
          </Typography>
                      <Box mt={2} mb={2}display="flex" justifyContent="start">
            <Button 
              onClick={() => window.open("https://drive.google.com/file/d/1JYTL-xotJzi-TPjS42jx0PQCwezIH3xt/view?usp=sharing", "_blank")}
              sx={{
                backgroundColor: "rgba(33, 146, 151, 0.15)",
                color: "#219297",
                fontWeight: "bold",
                textTransform: "none",
                px: 4,
                py: 1.5,
                borderRadius: "12px",
                border: "2px solid rgba(33, 146, 151, 0.3)",
                backdropFilter: "blur(10px)",
                fontSize: "16px",
                transition: "all 0.3s ease",
                "&:hover": {
                  backgroundColor: "rgba(33, 146, 151, 0.25)",
                  border: "2px solid rgba(33, 146, 151, 0.5)",
                  transform: "scale(1.05)",
                  color:"#219197"
                }
              }}
            >
             Watch Detailed Video
            </Button>
          </Box>

          {[
            {
              step: "1",
              title: "Create or Select a Project",
              content: (
                <>
                  Go to{" "}
                  <Link
                    href="https://console.cloud.google.com/"
                    sx={{ color: "#219297", fontWeight: 600 }}
                    target="_blank"
                    rel="noopener"
                  >
                    Google Cloud Console
                  </Link>{" "}
                  and sign in. Click the project dropdown at the top, then create a new project or select an existing one.
                </>
              ),
            },
            {
              step: "2",
              title: "Enable Google Calendar API",
              content: "From the left menu, navigate to APIs & Services → Library. Search for Google Calendar API, click it, then click Enable.",
            },
            {
              step: "3",
              title: "Configure OAuth Consent Screen",
              content: (
                <>
                  Go to APIs & Services → OAuth consent screen. Choose External, then enter your app name (e.g., Ansera Integration), user support email, and developer contact info. Save and add your Google account under Test Users.
                </>
              ),
            },
            {
              step: "4",
              title: "Create OAuth 2.0 Credentials",
              content: (
                <>
                  Navigate to APIs & Services → Credentials, then click Create Credentials → OAuth client ID. Choose Web Application, set a name, and add the Redirect URI provided by your administrator. After creating, note that credentials are managed securely on our servers.
                </>
              ),
            },
            {
              step: "5",
              title: "Connect to Ansera",
              content: (
                <>
                  Click the "Connect to Google Calendar" button above. You'll be redirected to Google to grant permissions. Once authorized, you'll be redirected back and your calendar will be connected automatically.
                </>
              ),
            },
          ].map(({ step, title, content }) => (
            <Box key={step} mb={3}>
              <Box display="flex" alignItems="center" gap={2} mb={1}>
                <Box
                  sx={{
                    width: 32,
                    height: 32,
                    borderRadius: "50%",
                    background: "linear-gradient(135deg, #219297 0%, #1a767a 100%)",
                    color: "#fff",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    fontWeight: "bold",
                    fontSize: "0.9rem",
                  }}
                >
                  {step}
                </Box>
                <Typography variant="body1" fontWeight={700} sx={{ color: "#344767" }}>
                  {title}
                </Typography>
              </Box>
              <Typography variant="body2" sx={{ color: "#555", ml: 6 }}>
                {content}
              </Typography>
            </Box>
          ))}
        </DialogContent>
        <DialogActions sx={{ p: 2.5 }}>
          <Button
            onClick={() => setHelpDialogOpen(false)}
            sx={{ 
              color: "#219297", 
              fontWeight: "bold", 
              textTransform: "none",
              px: 3,
              "&:hover": { backgroundColor: "rgba(33, 146, 151, 0.1)" },
            }}
          >
            Got it, thanks!
          </Button>
        </DialogActions>
      </Dialog>
    </SoftBox>
  );
};

export default Integrations;
